/**
 * The one way a launcher opens or closes the dock, so "is Kelly showing" is derived from the slice rather than
 * tracked twice. The control that opened it is recorded separately, in `dock-opener.tsx` — ADR 0019 leaves focus
 * restoration to the change that ships the launchers, which is this one.
 */

import { useCallback, useEffect } from "react"
import { captureMessage } from "@sentry/react"
import { useHaveEnabledFeaturesFailed } from "resources/account"
import { useAppDispatch, useAppSelector } from "hooks/redux"
import { selectDockView, setDockView } from "../store/agent-chat-slice"
import { useKellyChatEntitlement } from "./kelly-chat-context"

// One report per page load, across all five shells' launchers: the condition is an account-wide fact, and a
// mount-scoped latch would re-report on every navigation.
let hasReportedHiddenLauncher = false

export interface KellyDockToggle {
  /** Whether a launcher may be rendered at all. */
  isAvailable: boolean
  isOpen: boolean
  toggle: () => void
}

export function useKellyDockToggle(): KellyDockToggle {
  const dispatch = useAppDispatch()
  const dockView = useAppSelector(selectDockView)
  // Entitlement, never a non-null controller: the gate mounts an idle one for every account on every app page,
  // so a null check would show the launcher to accounts that have no Kelly.
  const entitlement = useKellyChatEntitlement()
  const haveFeaturesFailed = useHaveEnabledFeaturesFailed()

  /**
   * `isAvailable` is `"confirmed"` only — a launcher on `"unknown"` is deliberately rejected, not overlooked.
   * The dock is gated on `"confirmed"` too (ADR 0018), so a launcher rendered on `"unknown"` would be a control
   * that visibly does nothing; and in the healthy case `"unknown"` lasts one round trip, so the cost is a
   * launcher that appears a beat late rather than an account losing Kelly. Changing the RESOLVER instead would
   * be worse: accepting a terminal ERROR as `"denied"` claims an answer we do not have, and diverges from the
   * route gate, which passes through on ERROR.
   *
   * What that costs, and why the report exists: a features fetch that fails terminally pins entitlement at
   * `"unknown"` forever, so the launcher never appears at all — no error, no empty state, nothing in telemetry.
   * `/kelly-ai` has a surface to say so on (#4225); this path has none, so it says so to Sentry instead.
   */
  useEffect(() => {
    if (!haveFeaturesFailed || hasReportedHiddenLauncher) return
    hasReportedHiddenLauncher = true
    captureMessage("Kelly launcher hidden: enabled-features failed, so entitlement never resolves", "warning")
  }, [haveFeaturesFailed])

  const isOpen = dockView !== "closed"
  const toggle = useCallback(() => { dispatch(setDockView(isOpen ? "closed" : "docked")) }, [dispatch, isOpen])

  return { isAvailable: entitlement === "confirmed", isOpen, toggle }
}
